import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Lock, Mail, Phone, Briefcase, Building2,
  DollarSign, Clock, ExternalLink, FileText, CheckCircle, Circle,
  X, AlertCircle, Star, Award, Calendar, Send, Users, Shield
} from 'lucide-react';
import { candidatesAPI } from '../../services/api';
import { toast } from 'react-toastify';

// ── Role resolution (same pattern as App.js) ────────────────────────────────
const getEffectiveRole = () => {
  try {
    const raw = localStorage.getItem('userProfile');
    if (raw) {
      const profile = JSON.parse(raw);
      const r = profile.role || (Array.isArray(profile.roles) ? profile.roles[0] : null);
      if (r) return String(r).toUpperCase().trim().replace(/^ROLE_/, '');
    }
  } catch { /* ignore */ }
  const raw = localStorage.getItem('userRole') || '';
  return raw.replace(/^[\]"]+|[\]"]+$/g, '').toUpperCase().trim().replace(/^ROLE_/, '');
};

// ── Pipeline stage order ────────────────────────────────────────────────────
const PIPELINE_STAGES = [
  { key: 'applied',             label: 'Applied' },
  { key: 'shortlisted',         label: 'Shortlisted' },
  { key: 'slots_sent',          label: 'Slots Sent' },
  { key: 'slot_confirmed',      label: 'Slot Confirmed' },
  { key: 'interview_scheduled', label: 'Scheduled' },
  { key: 'offered',             label: 'Offered' },
  { key: 'joined',              label: 'Joined' },
];

const getStageIndex = (candidate) => {
  const status = (candidate.status || '').toLowerCase().replace(/_/g, ' ');
  const pipelineStage = (candidate.pipelineStage || '').toLowerCase().replace(/_/g, ' ');
  const combined = pipelineStage || status;
  if (combined.includes('join')) return 6;
  if (combined.includes('offer') || combined.includes('select')) return 5;
  if (combined.includes('interview scheduled') || combined.includes('scheduled')) return 4;
  if (combined.includes('slot confirmed') || combined.includes('confirmed')) return 3;
  if (combined.includes('slot') || combined.includes('screening')) return 2;
  if (combined.includes('shortlist')) return 1;
  return 0;
};

// ── Field helpers (same as CandidatePreview.js) ──────────────────────────────
const EXCLUDED_FIELDS = [
  'technicalSkills', 'softSkills', 'languages', 'preferredLocations',
  'keyAchievements', 'awardsRecognition', 'coverLetterUrl',
  'profilePhotoUrl', 'certificationsUpload', 'supportingDocuments',
  'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'id', 'organizationId',
  'jobIds', 'applicationId', 'clientIds', 'status', 'screeningQuestionsByJob',
  'professionalLicenses', 'areaProfessionalCertifications', 'resumeUrl',
  'stageHistory', 'pipelineStage', 'reason', 'timeslots',
  'selectedSlot', 'meetLink', 'clientTimeZone', 'userTimeZone',
  'statusLockedByClient', 'statusSetBy',
];

const formatFieldName = (fieldName) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

const getCategoryFromFieldName = (fieldName) => {
  const f = fieldName.toLowerCase();
  if (f.includes('name') || f === 'gender' || f === 'age') return 'Personal';
  if (f.includes('email') || f.includes('phone') || f.includes('linkedin') || f.includes('whatsapp')) return 'Contact';
  if (f === 'city' || f === 'state' || f === 'country' || f.includes('postal') || f === 'province') return 'Address';
  if (f.includes('currentjob') || f.includes('currentcompany') || f.includes('experience') || f.includes('notice')) return 'Employment';
  if (f.includes('ctc') || f.includes('salary') || f.includes('compensation')) return 'Compensation';
  if (f.includes('education') || f.includes('university') || f.includes('degree') || f.includes('gpa')) return 'Education';
  if (f.includes('consent') || f.includes('confirm') || f.includes('verification')) return 'Consents';
  return 'Other';
};

const formatValue = (value, fieldName) => {
  if (value === null || value === undefined || value === '') return '-';
  if (fieldName.toLowerCase().includes('date') || fieldName === 'dob') {
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { /* ignore */ }
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return value;
};

// ── Status badge color ───────────────────────────────────────────────────────
const getStatusColor = (status) => {
  const s = (status || '').toLowerCase().replace(/_/g, ' ');
  if (s.includes('join')) return 'bg-emerald-100 text-emerald-800';
  if (s.includes('offer')) return 'bg-teal-100 text-teal-800';
  if (s.includes('select')) return 'bg-green-100 text-green-800';
  if (s.includes('reject')) return 'bg-red-100 text-red-800';
  if (s.includes('interview')) return 'bg-yellow-100 text-yellow-800';
  if (s.includes('shortlist')) return 'bg-blue-100 text-blue-800';
  if (s.includes('screen')) return 'bg-purple-100 text-purple-800';
  return 'bg-gray-100 text-gray-800';
};

// ── Status options for override dropdown ─────────────────────────────────────
const STATUS_OPTIONS = [
  'Applied', 'Screening', 'Shortlisted', 'Interview_Scheduled',
  'Interview_Completed', 'Selected', 'Offered', 'Joined', 'Rejected', 'On_Hold',
];

// ── Main component ────────────────────────────────────────────────────────────
const CandidatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = getEffectiveRole();

  const [candidate, setCandidate] = useState(null);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('journey');

  // Override modal state
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideStatus, setOverrideStatus] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overriding, setOverriding] = useState(false);

  // Reject confirmation state
  const [rejecting, setRejecting] = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [candRes, fieldsRes] = await Promise.all([
          candidatesAPI.getApplicationById(id),
          candidatesAPI.getFields(),
        ]);
        setCandidate(candRes.data);
        const raw = Array.isArray(fieldsRes.data) ? fieldsRes.data : [];
        const configs = raw
          .map((item, idx) => {
            const name = typeof item === 'string' ? item : (item.fieldName || item.name || '');
            return {
              fieldName: name,
              displayName: typeof item === 'object' ? (item.displayName || formatFieldName(name)) : formatFieldName(name),
              category: typeof item === 'object' ? (item.category || getCategoryFromFieldName(name)) : getCategoryFromFieldName(name),
              orderIndex: typeof item === 'object' ? (item.orderIndex ?? idx) : idx,
            };
          })
          .filter(f => f.fieldName && !EXCLUDED_FIELDS.includes(f.fieldName))
          .sort((a, b) => a.orderIndex - b.orderIndex);
        setFieldConfigs(configs);
      } catch (err) {
        console.error('CandidatePage load error:', err);
        setError('Failed to load candidate details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const isLocked = candidate?.statusLockedByClient === true;
  const isAdmin = role === 'ADMIN';
  const stageIndex = candidate ? getStageIndex(candidate) : 0;

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!candidate) return;
    setRejecting(true);
    try {
      await candidatesAPI.updateApplicationStatus(candidate.id, { status: 'Rejected' });
      toast.success('Candidate marked as Rejected');
      const res = await candidatesAPI.getApplicationById(id);
      setCandidate(res.data);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to update status';
      if (err?.response?.status === 403) {
        toast.error('Status locked by client. Contact admin to override.');
      } else {
        toast.error(msg);
      }
    } finally {
      setRejecting(false);
    }
  };

  const handleOverrideConfirm = async () => {
    if (!overrideStatus || !overrideReason.trim()) {
      toast.error('Both status and reason are required.');
      return;
    }
    setOverriding(true);
    try {
      await candidatesAPI.updateApplicationStatus(candidate.id, {
        status: overrideStatus,
        reason: overrideReason.trim(),
      });
      toast.success('Status overridden successfully');
      setShowOverrideModal(false);
      setOverrideStatus('');
      setOverrideReason('');
      const res = await candidatesAPI.getApplicationById(id);
      setCandidate(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Override failed');
    } finally {
      setOverriding(false);
    }
  };

  // ── Profile: group fields by category ─────────────────────────────────────
  const CATEGORY_ORDER = ['Personal', 'Contact', 'Address', 'Employment', 'Compensation', 'Education', 'Work Authorization', 'Consents', 'Other'];
  const categoriesWithFields = (() => {
    const map = {};
    fieldConfigs.forEach(f => {
      const cat = f.category || 'Other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(f);
    });
    return CATEGORY_ORDER
      .filter(c => map[c] && map[c].length > 0)
      .map(c => ({ category: c, fields: map[c] }))
      .concat(
        Object.keys(map)
          .filter(c => !CATEGORY_ORDER.includes(c))
          .map(c => ({ category: c, fields: map[c] }))
      );
  })();

  // ── Loading / error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading candidate details…</p>
        </div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-gray-700 font-semibold">{error || 'Candidate not found'}</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 hover:underline text-sm">Go back</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'journey',   label: 'Journey' },
    { key: 'profile',   label: 'Profile' },
    { key: 'screening', label: 'Screening' },
    { key: 'documents', label: 'Documents' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
              {(candidate.firstName || candidate.fullName || '?')[0].toUpperCase()}
            </div>

            {/* Name + status + meta */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900">
                  {[candidate.firstName, candidate.middleName, candidate.lastName].filter(Boolean).join(' ') || candidate.fullName || 'Unknown'}
                </h1>
                {candidate.status && (
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(candidate.status)}`}>
                    {candidate.status.replace(/_/g, ' ')}
                  </span>
                )}
                {isLocked && <Lock size={16} className="text-gray-500" title="Status locked by client" />}
              </div>

              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
                {candidate.primaryEmail && (
                  <span className="flex items-center gap-1"><Mail size={13} />{candidate.primaryEmail}</span>
                )}
                {candidate.primaryPhone && (
                  <span className="flex items-center gap-1"><Phone size={13} />{candidate.primaryPhone}</span>
                )}
              </div>

              <div className="flex items-center gap-6 mt-2 text-sm text-gray-500 flex-wrap">
                {candidate.currentJobTitle && (
                  <span className="flex items-center gap-1"><Briefcase size={13} />{candidate.currentJobTitle}</span>
                )}
                {candidate.currentCompany && (
                  <span className="flex items-center gap-1"><Building2 size={13} />{candidate.currentCompany}</span>
                )}
                {candidate.currentCTC && (
                  <span className="flex items-center gap-1"><DollarSign size={13} />₹{Number(candidate.currentCTC).toLocaleString()}</span>
                )}
                {candidate.noticePeriod && (
                  <span className="flex items-center gap-1"><Clock size={13} />{candidate.noticePeriod} notice</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── PIPELINE STRIP ─────────────────────────────────────────────────── */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-0 overflow-x-auto">
            {PIPELINE_STAGES.map((stage, idx) => {
              const done    = idx < stageIndex;
              const current = idx === stageIndex;
              return (
                <React.Fragment key={stage.key}>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      done    ? 'bg-green-500 text-white' :
                      current ? 'bg-blue-600 text-white animate-pulse' :
                                'bg-gray-200 text-gray-400'
                    }`}>
                      {done ? <CheckCircle size={14} /> : current ? <Circle size={14} /> : <Circle size={14} />}
                    </div>
                    <span className={`text-xs mt-1 whitespace-nowrap ${
                      done ? 'text-green-600 font-medium' : current ? 'text-blue-600 font-semibold' : 'text-gray-400'
                    }`}>
                      {stage.label}
                    </span>
                  </div>
                  {idx < PIPELINE_STAGES.length - 1 && (
                    <div className={`h-0.5 flex-1 min-w-6 mx-1 ${idx < stageIndex ? 'bg-green-400' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── TABS ───────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-0">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── TAB CONTENT ────────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* ── TAB 1: Journey ─────────────────────────────────────────────── */}
        {activeTab === 'journey' && (
          <div className="flex gap-6">

            {/* Left panel — Timeline */}
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Timeline</h2>

              {/* Confirmed slot card */}
              {candidate.selectedSlot && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-1">Confirmed Slot</p>
                  <p className="text-sm font-semibold text-green-900">{candidate.selectedSlot}</p>
                </div>
              )}

              {/* Meeting link */}
              {candidate.meetLink && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">Meeting Link</p>
                    <a
                      href={candidate.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      Join meeting <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              )}

              {/* Offered timeslots (when no slot confirmed yet) */}
              {candidate.timeslots && candidate.timeslots.length > 0 && !candidate.selectedSlot && (
                <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs font-medium text-indigo-700 uppercase tracking-wide mb-2">Offered Slots</p>
                  <ul className="space-y-1">
                    {candidate.timeslots.map((slot, i) => (
                      <li key={i} className="text-sm text-indigo-800 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-indigo-200 text-indigo-700 text-xs flex items-center justify-center font-bold flex-shrink-0">{i + 1}</span>
                        {slot}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Stage history entries */}
              {candidate.stageHistory && candidate.stageHistory.length > 0 ? (
                <div className="space-y-3">
                  {candidate.stageHistory.map((entry, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mt-1 flex-shrink-0" />
                        {idx < candidate.stageHistory.length - 1 && (
                          <div className="w-px flex-1 bg-gray-200 mt-1" />
                        )}
                      </div>
                      <div className="pb-4 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900">{entry.stage || 'Stage change'}</span>
                          {entry.setBy === 'CLIENT' && (
                            <span className="text-xs text-blue-600 font-medium">Set by client</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                          {entry.changedAt && (
                            <span>{new Date(entry.changedAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          )}
                          {entry.changedBy && <span>by {entry.changedBy}</span>}
                        </div>
                        {entry.reason && (
                          <p className="text-xs text-gray-600 mt-1 italic">Reason: {entry.reason}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No timeline entries yet.</p>
              )}
            </div>

            {/* Right panel — Do next */}
            <div className="w-52 flex-shrink-0">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Do next</h2>

              {isLocked && !isAdmin ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
                  Status locked by client. Contact admin to override.
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Schedule meeting — only if selectedSlot exists and no meetLink yet */}
                  {candidate.selectedSlot && !candidate.meetLink && (
                    <button
                      onClick={() => toast.info('Interview scheduling coming soon.')}
                      className="w-full px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Calendar size={14} /> Schedule meeting
                    </button>
                  )}

                  {/* Send slots — only if no timeslots yet */}
                  {!candidate.timeslots?.length && (
                    <button
                      onClick={() => navigate('/ats/candidatesharing')}
                      className="w-full px-3 py-2 text-sm font-medium border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
                    >
                      <Send size={14} /> Send slots
                    </button>
                  )}

                  {/* Share with client */}
                  <button
                    onClick={() => navigate('/ats/candidatesharing')}
                    className="w-full px-3 py-2 text-sm font-medium border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
                  >
                    <Users size={14} /> Share with client
                  </button>

                  {/* Reject — hidden if locked and non-admin */}
                  {(!isLocked || isAdmin) && (
                    <button
                      onClick={handleReject}
                      disabled={rejecting}
                      className="w-full px-3 py-2 text-sm font-medium border border-red-400 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                    >
                      <X size={14} /> {rejecting ? 'Rejecting…' : 'Reject'}
                    </button>
                  )}

                  {/* Override — only for ADMIN when locked */}
                  {isAdmin && isLocked && (
                    <button
                      onClick={() => setShowOverrideModal(true)}
                      className="w-full px-3 py-2 text-sm font-medium border border-red-600 text-red-700 rounded-lg hover:bg-red-50 flex items-center gap-2"
                    >
                      <Shield size={14} /> Override client decision
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB 2: Profile ─────────────────────────────────────────────── */}
        {activeTab === 'profile' && (
          <div className="space-y-8">
            {categoriesWithFields.length === 0 ? (
              <p className="text-sm text-gray-400">No fields configured for this organisation.</p>
            ) : (
              categoriesWithFields.map(({ category, fields }) => (
                <section key={category} className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-900">{category}</h3>
                    <span className="text-xs text-gray-400">({fields.length})</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {fields.map(field => (
                      <div key={field.fieldName} className="p-4 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          {field.displayName || formatFieldName(field.fieldName)}
                        </p>
                        <p className="text-sm font-semibold text-gray-900 break-words">
                          {formatValue(candidate[field.fieldName], field.fieldName)}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              ))
            )}

            {/* Technical skills */}
            {candidate.technicalSkills && candidate.technicalSkills.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">Technical Skills</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {candidate.technicalSkills.map((skill, i) => (
                    <div key={i} className="px-3 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium flex items-center gap-2">
                      <span>{skill.skillName || skill}</span>
                      {skill.rating > 0 && (
                        <div className="flex">
                          {[...Array(5)].map((_, j) => (
                            <Star key={j} size={10} className={j < skill.rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Soft skills */}
            {candidate.softSkills && candidate.softSkills.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">Soft Skills</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {candidate.softSkills.map((skill, i) => (
                    <span key={i} className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      {skill.skillName || skill}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Languages */}
            {candidate.languages && candidate.languages.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">Languages</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {candidate.languages.map((lang, i) => (
                    <span key={i} className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                      {lang.language || lang}
                      {lang.proficiency && ` (${lang.proficiency})`}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* ── TAB 3: Screening ───────────────────────────────────────────── */}
        {activeTab === 'screening' && (
          <div>
            {candidate.screeningQuestionsByJob && Object.keys(candidate.screeningQuestionsByJob).length > 0 ? (
              <div className="space-y-6">
                {Object.entries(candidate.screeningQuestionsByJob).map(([jobId, questions]) => (
                  <section key={jobId} className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-700 bg-blue-50 px-3 py-2 rounded-lg">
                      <Briefcase size={14} />
                      <span>Job: {jobId}</span>
                    </div>
                    <div className="space-y-3">
                      {Array.isArray(questions) && questions.map((qa, i) => (
                        <div key={i} className="bg-white p-4 rounded-lg border border-gray-100">
                          <div className="flex items-start gap-3">
                            <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 text-sm mb-2">{qa.question}</p>
                              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                                <CheckCircle size={15} className="text-blue-600 flex-shrink-0 mt-0.5" />
                                <p className="text-gray-800 text-sm">{qa.answer || '-'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <AlertCircle size={40} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No screening answers recorded</p>
              </div>
            )}
          </div>
        )}

        {/* ── TAB 4: Documents ───────────────────────────────────────────── */}
        {activeTab === 'documents' && (
          <div className="space-y-3">
            {[
              { url: candidate.resumeUrl,              label: 'Resume / CV',        icon: FileText },
              { url: candidate.coverLetterUrl,         label: 'Cover Letter',       icon: FileText },
              { url: candidate.certificationsUpload,   label: 'Certifications',     icon: Award },
              { url: candidate.supportingDocuments,    label: 'Supporting Docs',    icon: FileText },
            ].filter(doc => doc.url).map((doc, i) => {
              const Icon = doc.icon;
              return (
                <a
                  key={i}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-100 hover:border-blue-300 hover:shadow-sm transition-all group"
                >
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100">
                    <Icon size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{doc.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Click to open in new tab</p>
                  </div>
                  <ExternalLink size={16} className="text-gray-400 group-hover:text-blue-600" />
                </a>
              );
            })}
            {!candidate.resumeUrl && !candidate.coverLetterUrl && !candidate.certificationsUpload && !candidate.supportingDocuments && (
              <div className="text-center py-16">
                <FileText size={40} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No documents uploaded</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── OVERRIDE MODAL ─────────────────────────────────────────────────── */}
      {showOverrideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Override Client Decision</h3>
              <button onClick={() => setShowOverrideModal(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Status <span className="text-red-500">*</span></label>
                <select
                  value={overrideStatus}
                  onChange={e => setOverrideStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select status…</option>
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s === 'Selected' ? 'Selected / Hired' : s.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Override <span className="text-red-500">*</span></label>
                <textarea
                  value={overrideReason}
                  onChange={e => setOverrideReason(e.target.value)}
                  rows={3}
                  placeholder="Mandatory — explain why the client decision is being overridden"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowOverrideModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleOverrideConfirm}
                disabled={overriding || !overrideStatus || !overrideReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {overriding ? 'Overriding…' : 'Confirm Override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidatePage;
